import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, Heart, Users, Calendar, Check, ArrowRight, Sparkles, Search, X, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/config/apiBase";

/**
 * OnboardingFlow - Guides new users to their first successful call loop
 * 
 * Flow:
 * 1. Welcome screen with value prop
 * 2. Add first contact (Name + Phone required)
 * 3. Set up first reminder (Call, 30-day cadence)
 * 4. Success screen
 */
const OnboardingFlow = ({ onComplete, userName }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Contact form state
  const [contactData, setContactData] = useState({
    name: "",
    phone: "",
    email: "",
    reminder_frequency_days: 30,
    birthday_month: null,
    birthday_day: null,
    birthday_year: null
  });
  
  // Created contact reference
  const [createdContact, setCreatedContact] = useState(null);
  
  // Google Contacts Import State
  const [showGooglePicker, setShowGooglePicker] = useState(false);
  const [googleContacts, setGoogleContacts] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Check Google connection on mount
  useEffect(() => {
    checkGoogleConnection();
    
    // Check for OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_contacts_success") === "true") {
      setGoogleConnected(true);
      window.history.replaceState({}, "", window.location.pathname);
      // Auto-open picker after successful OAuth
      handleOpenGooglePicker(true);
    }
  }, []);

  const checkGoogleConnection = async () => {
    try {
      const response = await axios.get(`${API}/google/contacts/status`);
      setGoogleConnected(response.data.connected);
    } catch (error) {
      console.error("Failed to check Google status:", error);
    }
  };

  const handleGoogleImport = async () => {
    if (googleConnected) {
      handleOpenGooglePicker(false);
    } else {
      // Start OAuth flow
      try {
        const response = await axios.get(`${API}/google/contacts/auth-url`);
        window.location.href = response.data.auth_url;
      } catch (error) {
        if (error.response?.status === 503) {
          toast.error("Google import is not available right now.");
        } else {
          toast.error("We couldn't connect to Google. Please try again.");
        }
      }
    }
  };

  const handleOpenGooglePicker = async (fromCallback = false) => {
    console.log("[Onboarding] Opening Google picker dialog");
    setShowGooglePicker(true);
    setGoogleLoading(true);
    
    try {
      const response = await axios.get(`${API}/google/contacts/list`);
      console.log("[Onboarding] Google contacts loaded:", { count: response.data.contacts?.length });
      setGoogleContacts(response.data.contacts);
    } catch (error) {
      if (error.response?.status === 401) {
        setGoogleConnected(false);
        setShowGooglePicker(false);
        toast.error("Google connection expired. Please reconnect.");
      } else {
        toast.error("Failed to load contacts from Google.");
        setShowGooglePicker(false);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSelectGoogleContact = (contact) => {
    // Extract primary phone (prefer mobile)
    let phone = "";
    if (contact.phones && contact.phones.length > 0) {
      phone = contact.phones[0];
    }
    
    // Extract primary email
    let email = "";
    if (contact.emails && contact.emails.length > 0) {
      email = contact.emails[0];
    }
    
    // Extract birthday
    let birthday_month = null;
    let birthday_day = null;
    let birthday_year = null;
    
    if (contact.birthday) {
      try {
        const parts = contact.birthday.split("-");
        if (parts.length >= 3) {
          const year = parseInt(parts[0]);
          birthday_month = parseInt(parts[1]);
          birthday_day = parseInt(parts[2]);
          if (year && year > 1900) {
            birthday_year = year;
          }
        }
      } catch {
        // Ignore parsing errors
      }
    }
    
    // Auto-populate form
    setContactData({
      ...contactData,
      name: contact.full_name || "",
      phone: phone,
      email: email,
      birthday_month,
      birthday_day,
      birthday_year
    });
    
    setShowGooglePicker(false);
    setSearchQuery("");
    
    toast.success(`Imported ${contact.full_name}!`);
  };

  // Filter contacts by search
  const filteredGoogleContacts = googleContacts.filter(contact => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      contact.full_name?.toLowerCase().includes(query) ||
      contact.emails?.some(e => e.toLowerCase().includes(query)) ||
      contact.phones?.some(p => p.includes(query))
    );
  });

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!contactData.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!contactData.phone?.trim()) {
      toast.error("Phone number is required");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contacts`, {
        name: contactData.name.trim(),
        phone: contactData.phone.trim(),
        email: contactData.email?.trim() || null,
        reminder_frequency_days: 30,
        birthday_month: contactData.birthday_month,
        birthday_day: contactData.birthday_day,
        birthday_year: contactData.birthday_year
      });
      
      setCreatedContact(response.data);
      toast.success(`Added ${response.data.name}!`);
      setStep(3); // Go to reminder setup
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || "Failed to add contact");
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error("Failed to add contact");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetReminder = async () => {
    // The contact was already created with reminder_frequency_days = 30
    // The backend automatically sets next_reminder = now + 7 days for new contacts
    // We just need to proceed to success
    setStep(4);
  };

  const handleSkipReminder = async () => {
    // User chose to skip reminder setup
    // Update the contact to disable reminders (set frequency to null)
    if (createdContact) {
      try {
        await axios.put(`${API}/contacts/${createdContact.contact_id}`, {
          reminder_frequency_days: null
        });
      } catch (error) {
        // Silently fail - don't block onboarding for this
        console.error("Failed to disable reminder:", error);
      }
      
      // Store flag for one-time dashboard nudge
      localStorage.setItem("onboarding_skipped_reminder", JSON.stringify({
        contact_id: createdContact.contact_id,
        contact_name: createdContact.name
      }));
    }
    
    // Proceed to success
    setStep(4);
  };

  const handleComplete = () => {
    onComplete();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF8F4] via-white to-[#F4F1EA] flex items-center justify-center p-4">
      <AnimatePresence mode="wait">
        {/* Step 1: Welcome */}
        {step === 1 && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-md w-full text-center"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#F4F1EA]">
              {/* Icon */}
              <div className="w-20 h-20 bg-gradient-to-br from-[#E07A5F] to-[#81B29A] rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-10 h-10 text-white" />
              </div>
              
              {/* Headline */}
              <h1 
                className="text-3xl font-bold text-[#3D405B] mb-3"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Stay connected without remembering everything.
              </h1>
              
              {/* Subtext */}
              <p className="text-[#6D6F7C] mb-8">
                Add one person you don&apos;t want to lose touch with.
              </p>
              
              {/* Features preview */}
              <div className="flex justify-center gap-6 mb-8 text-sm text-[#6D6F7C]">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#81B29A]" />
                  <span>Reminders</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#E07A5F]" />
                  <span>Occasions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#F2CC8F]" />
                  <span>Streaks</span>
                </div>
              </div>
              
              {/* CTA */}
              <Button
                onClick={() => setStep(2)}
                className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full py-6 text-lg"
                data-testid="onboarding-start-btn"
              >
                Add your first contact
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Add Contact */}
        {step === 2 && (
          <motion.div
            key="add-contact"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-md w-full"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#F4F1EA]">
              {/* Progress */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
                <div className="w-12 h-1 bg-[#F4F1EA] rounded-full" />
                <div className="w-8 h-8 bg-[#F4F1EA] text-[#6D6F7C] rounded-full flex items-center justify-center text-sm font-medium">2</div>
                <div className="w-12 h-1 bg-[#F4F1EA] rounded-full" />
                <div className="w-8 h-8 bg-[#F4F1EA] text-[#6D6F7C] rounded-full flex items-center justify-center text-sm font-medium">3</div>
              </div>
              
              <h2 
                className="text-2xl font-bold text-[#3D405B] mb-2 text-center"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Who do you want to stay in touch with?
              </h2>
              <p className="text-[#6D6F7C] text-center mb-6">
                Pick someone important — a parent, old friend, or mentor.
              </p>
              
              {/* Google Import Button - Primary CTA */}
              <div className="mb-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleImport}
                  className="w-full border-2 border-[#4285F4]/30 hover:border-[#4285F4] hover:bg-[#4285F4]/5 rounded-xl h-12 justify-center gap-3"
                  data-testid="onboarding-google-import-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Import from Google Contacts
                </Button>
                <p className="text-xs text-[#6D6F7C] text-center mt-2">
                  Save time — import from Google
                </p>
              </div>
              
              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <Separator className="flex-1" />
                <span className="text-xs text-[#9CA3AF]">or enter manually</span>
                <Separator className="flex-1" />
              </div>
              
              <form onSubmit={handleAddContact} className="space-y-4">
                <div>
                  <Label className="text-[#3D405B]">Name *</Label>
                  <Input
                    value={contactData.name}
                    onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                    className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                    placeholder="Mom, Dad, Best Friend..."
                    required
                    data-testid="onboarding-contact-name"
                  />
                </div>
                
                <div>
                  <Label className="text-[#3D405B]">Phone *</Label>
                  <Input
                    value={contactData.phone}
                    onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                    className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                    placeholder="+1 555 123 4567"
                    required
                    data-testid="onboarding-contact-phone"
                  />
                </div>
                
                <div>
                  <Label className="text-[#6D6F7C]">Email (optional)</Label>
                  <Input
                    type="email"
                    value={contactData.email}
                    onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                    className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                    placeholder="their@email.com"
                    data-testid="onboarding-contact-email"
                  />
                </div>
                
                {/* Show birthday if imported from Google */}
                {contactData.birthday_month && contactData.birthday_day && (
                  <div className="p-3 bg-[#F4F1EA]/50 rounded-xl">
                    <p className="text-sm text-[#6D6F7C]">
                      <span className="font-medium text-[#3D405B]">Birthday imported:</span>{" "}
                      {["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][contactData.birthday_month]} {contactData.birthday_day}
                      {contactData.birthday_year && `, ${contactData.birthday_year}`}
                    </p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 rounded-full border-[#F4F1EA]"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full"
                    data-testid="onboarding-save-contact-btn"
                  >
                    {isSubmitting ? "Saving..." : "Continue"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </form>
              
              {/* Google Contacts Picker Dialog */}
              <Dialog open={showGooglePicker} onOpenChange={(open) => { 
                setShowGooglePicker(open); 
                if (!open) setSearchQuery(""); 
              }}>
                <DialogContent className="sm:max-w-[500px] max-h-[70vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Select a contact to import
                    </DialogTitle>
                  </DialogHeader>
                  
                  {googleLoading ? (
                    <div className="py-12 text-center">
                      <RefreshCw className="w-8 h-8 text-[#E07A5F] animate-spin mx-auto mb-4" />
                      <p className="text-[#6D6F7C]">Loading your contacts...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col flex-1 min-h-0">
                      {/* Search */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input
                          placeholder="Search contacts..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 border-[#F4F1EA] rounded-xl"
                          data-testid="onboarding-search-contacts"
                        />
                      </div>
                      
                      {/* Contact count */}
                      <p className="text-xs text-[#6D6F7C] mb-2">
                        {filteredGoogleContacts.length} contact{filteredGoogleContacts.length !== 1 ? 's' : ''} found
                      </p>
                      
                      {/* Contact List */}
                      <div className="flex-1 overflow-y-auto border border-[#F4F1EA] rounded-xl divide-y divide-[#F4F1EA] min-h-[150px] max-h-[300px]">
                        {filteredGoogleContacts.length === 0 ? (
                          <div className="p-6 text-center text-[#6D6F7C]">
                            {searchQuery ? "No contacts match your search" : "No contacts found in your Google account"}
                          </div>
                        ) : (
                          filteredGoogleContacts.map((contact, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full flex items-center gap-3 p-3 hover:bg-[#F4F1EA]/50 transition-colors text-left"
                              onClick={() => handleSelectGoogleContact(contact)}
                              data-testid={`google-contact-${index}`}
                            >
                              <div className="w-10 h-10 bg-gradient-to-br from-[#E07A5F] to-[#81B29A] rounded-full flex items-center justify-center text-white font-medium">
                                {contact.full_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#3D405B] truncate">{contact.full_name}</p>
                                <div className="flex items-center gap-2 text-xs text-[#6D6F7C]">
                                  {contact.phones?.[0] && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {contact.phones[0]}
                                    </span>
                                  )}
                                  {contact.emails?.[0] && (
                                    <span className="truncate">
                                      {contact.emails[0]}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-[#9CA3AF]" />
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </motion.div>
        )}

        {/* Step 3: Set Reminder */}
        {step === 3 && createdContact && (
          <motion.div
            key="set-reminder"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-md w-full"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#F4F1EA]">
              {/* Progress */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#81B29A] text-white rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <div className="w-12 h-1 bg-[#81B29A] rounded-full" />
                <div className="w-8 h-8 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
                <div className="w-12 h-1 bg-[#F4F1EA] rounded-full" />
                <div className="w-8 h-8 bg-[#F4F1EA] text-[#6D6F7C] rounded-full flex items-center justify-center text-sm font-medium">3</div>
              </div>
              
              <h2 
                className="text-2xl font-bold text-[#3D405B] mb-2 text-center"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Your first reminder is set! 🎉
              </h2>
              
              <p className="text-[#6D6F7C] text-center mb-6">
                We&apos;ll remind you to call <span className="font-medium text-[#3D405B]">{createdContact.name}</span> in 7 days.
              </p>
              
              {/* Reminder preview card */}
              <div className="bg-[#F4F1EA]/50 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E07A5F] to-[#81B29A] rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {createdContact.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#3D405B]">{createdContact.name}</h3>
                    <p className="text-sm text-[#6D6F7C]">{createdContact.phone}</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-[#F4F1EA] flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-[#6D6F7C]">
                    <Phone className="w-4 h-4 text-[#81B29A]" />
                    <span>Call reminder</span>
                  </div>
                  <div className="text-sm font-medium text-[#E07A5F]">
                    Every 30 days
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-[#6D6F7C] text-center mb-6">
                When it&apos;s time, you&apos;ll see a <span className="font-medium">&quot;Call now&quot;</span> button. After the call, we&apos;ll help you log it.
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={handleSetReminder}
                  className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full py-6"
                  data-testid="onboarding-continue-btn"
                >
                  Create reminder
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <button
                  onClick={handleSkipReminder}
                  className="w-full text-[#6D6F7C] hover:text-[#3D405B] text-sm py-2 transition-colors"
                  data-testid="onboarding-skip-btn"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="max-w-md w-full text-center"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#F4F1EA]">
              {/* Progress complete */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#81B29A] text-white rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <div className="w-12 h-1 bg-[#81B29A] rounded-full" />
                <div className="w-8 h-8 bg-[#81B29A] text-white rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <div className="w-12 h-1 bg-[#81B29A] rounded-full" />
                <div className="w-8 h-8 bg-[#81B29A] text-white rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
              </div>
              
              {/* Celebration icon */}
              <div className="w-24 h-24 bg-gradient-to-br from-[#F2CC8F] to-[#E07A5F] rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-12 h-12 text-white" />
              </div>
              
              <h2 
                className="text-3xl font-bold text-[#3D405B] mb-3"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                You&apos;re set up! 🎉
              </h2>
              
              <p className="text-[#6D6F7C] mb-8">
                {userName ? `Nice work, ${userName.split(' ')[0]}!` : "Nice work!"} Your first reminder is ready. We&apos;ll make sure you never lose touch.
              </p>
              
              <Button
                onClick={handleComplete}
                className="w-full bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full py-6 text-lg"
                data-testid="onboarding-complete-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingFlow;
